import { useEffect, useMemo, useRef, useState } from 'react'
import Hls from 'hls.js'
import './CameraStreamPlayer.css'

type Props = {
  url: string | null | undefined
}

export default function CameraStreamPlayer({ url }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const [useImg, setUseImg] = useState(false)

  const trimmed = url?.trim() || ''

  const mjpegHint = useMemo(() => {
    const l = trimmed.toLowerCase()
    return (
      l.includes('mjpeg') ||
      l.includes('/mjpg') ||
      l.endsWith('.mjpg') ||
      (l.includes('snapshot') && l.includes('jpg'))
    )
  }, [trimmed])

  useEffect(() => {
    setUseImg(false)
  }, [trimmed])

  useEffect(() => {
    if (!trimmed || mjpegHint || useImg) return
    const lower = trimmed.toLowerCase()
    if (lower.startsWith('rtsp://') || lower.startsWith('rtsps://')) return

    const video = videoRef.current
    if (!video) return

    if (lower.includes('.m3u8')) {
      if (Hls.isSupported()) {
        const hls = new Hls({ enableWorker: true })
        hlsRef.current = hls
        hls.loadSource(trimmed)
        hls.attachMedia(video)
        return () => {
          hls.destroy()
          hlsRef.current = null
        }
      }
      video.src = trimmed
      return () => {
        video.removeAttribute('src')
        video.load()
      }
    }

    video.src = trimmed
    return () => {
      video.removeAttribute('src')
      video.load()
    }
  }, [trimmed, mjpegHint, useImg])

  if (!trimmed) {
    return (
      <div className="camera-placeholder">
        <p>לא הוגדר קישור למצלמה.</p>
        <p className="camera-hint">
          הזן כתובת HTTP/HTTPS (HLS ‎.m3u8‎, MJPEG, או MP4). RTSP דורש המרה ברחפן.
        </p>
      </div>
    )
  }

  if (trimmed.toLowerCase().startsWith('rtsp://') || trimmed.toLowerCase().startsWith('rtsps://')) {
    return (
      <div className="camera-placeholder camera-warning">
        <p>כתובת RTSP לא ניתנת לניגון ישיר בדפדפן.</p>
        <p className="camera-hint">
          הפעל ברחפן שער וידאו (למשל HLS או MJPEG מעל HTTP) והזן כאן את כתובת ה-HTTP.
        </p>
      </div>
    )
  }

  if (mjpegHint || useImg) {
    return (
      <div className="camera-frame">
        <img className="camera-feed" src={trimmed} alt="מצלמת רחפן" />
      </div>
    )
  }

  return (
    <div className="camera-frame">
      <video
        ref={videoRef}
        className="camera-feed"
        controls
        playsInline
        muted
        autoPlay
        onError={() => setUseImg(true)}
      />
      <p className="camera-hint-inline">
        אם אין תמונה, נסה זרם MJPEG או בדוק הרשאות רשת / CORS.
      </p>
    </div>
  )
}
